/**
 * Shared display formatting for the universal per-line ``dimensions``
 * dict (WS 3D). ONE formatter for every FE surface that shows a
 * material/line variant — order rows, quote tables, stock matrix,
 * batches, purchase orders, shortage panels.
 *
 * A short list of legacy keys keeps its historical bespoke rendering,
 * pixel-identical to the per-page whitelists this module replaced (see
 * ``LEGACY_KEY_ORDER`` — the only keys known by name, ratchet-pinned).
 *
 * Any OTHER key renders generically as ``label: value unit``. The
 * label/unit come from the optional ``labels`` map (callers with
 * ``public_input_dimensions`` / ``default_dimensions_schema`` /
 * material-property registry metadata build one); without an entry the
 * key itself is humanized (underscores → spaces). Per-process parameter
 * names are DB data — this module must never grow a per-process key
 * whitelist beyond the legacy quartet above.
 */

import i18next from "i18next";

export interface DimensionDisplayLabel {
  label: string;
  unit?: string | null;
  /** Human name of THIS line's value („Černá" for ``cerna``). When set it
   *  replaces the raw value and the unit (a value label is already
   *  complete — „3 mm"). */
  valueLabel?: string | null;
  /** Human names of the dimension's described values, keyed by value
   *  (``enum_options``, KAN-1432) — for callers that label a whole field,
   *  not one line. */
  valueLabels?: Readonly<Record<string, string>>;
}

/** dimension key → display metadata (label + optional unit). */
export type DimensionLabelMap = Map<string, DimensionDisplayLabel>;

// Legacy keys with bespoke rendering. The fixed order mirrors the
// historical per-page whitelists so existing surfaces stay byte-identical.
const LEGACY_KEY_ORDER: readonly string[] = [
  "thickness_mm",
  "filament_diameter_mm",
  "color_hex",
  "ral_code",
];

function humanizeKey(key: string): string {
  return key.replace(/_/g, " ");
}

/**
 * Format a dimensions dict for display. Empty/absent dict → "—".
 * Values that are ``null``/``undefined``/empty string are skipped.
 */
/** The display ORDER of a dimensions dict: ratchet-pinned legacy keys first,
 *  the rest in insertion order — shared with ``variantLabels`` so a
 *  label-aware render and the raw formatter agree on „2 mm · RAL 9005". */
export function orderedDimensionKeys(
  dimensions: Record<string, unknown>,
): string[] {
  return [
    ...LEGACY_KEY_ORDER.filter((k) => k in dimensions),
    ...Object.keys(dimensions).filter((k) => !LEGACY_KEY_ORDER.includes(k)),
  ];
}

/** A decimal that arrived as text („1.5" from an enum / JSON string). Only
 *  values WITH a decimal point qualify — an integer-looking string may be a
 *  code („6082"), and regrouping it („6 082") would be wrong. */
const DECIMAL_STRING = /^-?\d+\.\d+$/;

/** A value as a person reads it: numbers in the reader's locale („0,2").
 *  KAN-2101 (P24) — without an explicit ``locale`` the app language is used,
 *  so „1.5 mm" no longer leaks to Czech readers from callers that pass none. */
function displayValue(value: unknown, locale?: string): string {
  const lang = locale ?? i18next.language;
  const n =
    typeof value === "number"
      ? value
      : typeof value === "string" && DECIMAL_STRING.test(value.trim())
        ? Number(value.trim())
        : null;
  if (lang && n !== null && Number.isFinite(n)) {
    return new Intl.NumberFormat(lang, { maximumFractionDigits: 6 }).format(n);
  }
  return String(value);
}

export function formatDimensions(
  dimensions: Record<string, unknown> | null | undefined,
  labels?: DimensionLabelMap,
  locale?: string,
): string {
  if (!dimensions || Object.keys(dimensions).length === 0) {
    return "—";
  }
  const orderedKeys = [
    ...LEGACY_KEY_ORDER.filter((k) => k in dimensions),
    ...Object.keys(dimensions).filter((k) => !LEGACY_KEY_ORDER.includes(k)),
  ];
  const parts: string[] = [];
  for (const key of orderedKeys) {
    const value = dimensions[key];
    if (value == null || value === "") continue;
    if (key === "thickness_mm") {
      parts.push(`${displayValue(value, locale)} mm`);
      continue;
    }
    if (key === "filament_diameter_mm") {
      parts.push(`⌀ ${displayValue(value, locale)} mm`);
      continue;
    }
    if (key === "color_hex") {
      if (typeof value === "string") {
        parts.push(`#${value.replace(/^#/, "")}`);
      }
      continue;
    }
    if (key === "ral_code") {
      parts.push(`RAL ${String(value)}`);
      continue;
    }
    const meta = labels?.get(key);
    const label = meta?.label || humanizeKey(key);
    const valueLabel = meta?.valueLabel || meta?.valueLabels?.[String(value)];
    // Q14 — „název" hodnoty, který je jen ta sama hodnota („1.5" u 1.5,
    // syrový kód z registru), nic nepojmenovává: číslo jde v jazyce čtenáře
    // a s jednotkou, ne „Tloušťka: 1.5".
    if (valueLabel && !(Number(valueLabel) === Number(value) && valueLabel.trim() !== "")) {
      parts.push(`${label}: ${valueLabel}`);
      continue;
    }
    const unit = meta?.unit ? ` ${meta.unit}` : "";
    parts.push(`${label}: ${displayValue(value, locale)}${unit}`);
  }
  return parts.length ? parts.join(" · ") : "—";
}


/**
 * Build a {@link DimensionLabelMap} from ``default_dimensions_schema``
 * fields (``ProcessTypeDefault``) — the same rows the DimensionPicker
 * renders from. ``lang`` picks the cs/en label with cross-fallback.
 */
export function dimensionLabelsFromFields(
  fields: ReadonlyArray<{
    key: string;
    label_cs?: string | null;
    label_en?: string | null;
    unit?: string | null;
    enum_options?: unknown;
  }>,
  lang: string,
): DimensionLabelMap {
  const pick = (cs: unknown, en: unknown): string | null => {
    const first = lang.startsWith("cs") ? cs : en;
    const second = lang.startsWith("cs") ? en : cs;
    if (typeof first === "string" && first) return first;
    return typeof second === "string" && second ? second : null;
  };
  const map: DimensionLabelMap = new Map();
  for (const f of fields) {
    const label = pick(f.label_cs, f.label_en) ?? f.key;
    const valueLabels: Record<string, string> = {};
    for (const raw of Array.isArray(f.enum_options) ? f.enum_options : []) {
      const o = raw as Record<string, unknown> | null;
      const name = o && typeof o.value === "string" ? pick(o.label_cs, o.label_en) : null;
      if (o && name) valueLabels[String(o.value)] = name;
    }
    map.set(f.key, {
      label,
      unit: f.unit ?? null,
      ...(Object.keys(valueLabels).length > 0 ? { valueLabels } : {}),
    });
  }
  return map;
}

// Stock UoM display labels (previously copy-pasted per page). Unknown
// units render literally — the option list itself is BE data
// (``material_categories.default_stock_uom`` / the ``stock_uom`` property
// registry), tohle je jen hezčí zápis těch, které mají znak.
const STOCK_UOM_LABELS: Record<string, string> = {
  m2: "m²",
  kg: "kg",
  g: "g",
  l: "l",
  pcs: "ks",
  m: "m",
};

/** Prázdno = jednotku neurčuje ani položka, ani její kategorie ⇒ „—".
 *
 * NEVYMÝŠLET „m²". Dřív to tady stálo jako historický default plechu, takže
 * položka bez jednotky vypadala, že je vedená v m² — a admin, který jednotku
 * nastavil kategorii, nepoznal, že se k položce nedostala. */
export function formatStockUom(uom: string | null | undefined): string {
  if (!uom) return "—";
  return STOCK_UOM_LABELS[uom] ?? uom;
}

/** Q14 — množství skladu v jazyce čtenáře, bez koncových nul serverového
 *  Decimalu: „+1 000" místo „+1000.0000", „28" místo „28.0000".
 *  ``signed`` dá „+" před kladné číslo (pohyb skladu). Nečíselný vstup
 *  projde beze změny. Jen zobrazení — editační pole drží hodnotu, jak přišla. */
export function formatStockQty(
  value: string | number | null | undefined,
  { signed = false, locale }: { signed?: boolean; locale?: string } = {},
): string {
  if (value == null || value === "") return "";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  return new Intl.NumberFormat(locale ?? i18next.language ?? "cs", {
    maximumFractionDigits: 4,
    signDisplay: signed ? "exceptZero" : "auto",
  }).format(n);
}

/** Q14 — desetinná čísla uvnitř hotového popisku ze serveru (formát
 *  polotovaru „1.5×3 m") v jazyce čtenáře: „1,5×3 m". Celá čísla se
 *  nepřeskupují — „6082" může být kód. */
export function localizeDecimals(text: string, locale?: string): string {
  const lang = locale ?? i18next.language;
  if (!lang) return text;
  const fmt = new Intl.NumberFormat(lang, { maximumFractionDigits: 6, useGrouping: false });
  return text.replace(/\d+\.\d+/g, (m) => fmt.format(Number(m)));
}
